import React, { useEffect, forwardRef, useState, useRef } from 'react';
import { capitalize, cloneDeep, debounce } from 'lodash';
import Switch from 'components/Inputs/Switch';
import InputTitleWrap from 'components/Inputs/InputTitleWrap';
import { StatusEnum, type Time } from 'generated/graphql';
import { END_OF_DAY, START_OF_DAY } from 'consts';
import type { DayOfWeekType, DayOfWeekKey } from 'types';
import styles from './DayOfWeek.module.scss';

const hasEndOfDay = (timeRanges: Time[]): boolean => {
  const lastTimeRange = timeRanges[timeRanges.length - 1];
  return lastTimeRange?.to === END_OF_DAY || lastTimeRange?.to === '23:58'; // stop menu creation at 23:58 there is only 1 minute left.
};

const adjustMinutes = (adjustment: 'add' | 'subtract', num: number, timeString: string): string => {
  const [hours, minutes] = timeString.split(':').map(Number);
  const date = new Date();
  if (adjustment === 'add') {
    date.setHours(hours, minutes + num, 0, 0);
  } else {
    date.setHours(hours, minutes - num, 0, 0);
  }

  const newHours = String(date.getHours()).padStart(2, '0');
  const newMinutes = String(date.getMinutes()).padStart(2, '0');
  return `${newHours}:${newMinutes}`;
};

const autoCorrectTimeInput = (
  index: number,
  field: Exclude<keyof Time, '__typename'>,
  incomingTime: string,
  time: { from: string; to: string }[]
): string => {
  if (field === 'from' && index > 0) {
    if (incomingTime <= time[index - 1].to) {
      return adjustMinutes('add', 1, time[index - 1].to);
    }
  }

  if (field === 'to' && index < time.length - 1) {
    if (incomingTime >= time[index + 1].from) {
      return adjustMinutes('subtract', 1, time[index + 1].from);
    }
  }

  if (field === 'to' && incomingTime <= time[index].from) {
    return adjustMinutes('add', 1, time[index].from);
  }

  if (field === 'from' && incomingTime >= time[index].to) {
    return adjustMinutes('subtract', 1, time[index].to);
  }

  return incomingTime;
};

type DayOfWeekProps = {
  dayHours: DayOfWeekType;
  onChange: (dayHours: DayOfWeekType) => void;
};

const DayOfWeek = forwardRef<HTMLDivElement, DayOfWeekProps>(({ dayHours, onChange }, ref) => {
  const { day, hours, error } = dayHours;
  const { status, time } = hours;
  const isDayOpen = status === 'open';
  const newFromInputRef = useRef<HTMLInputElement | null>(null);

  const [showAddTimeRange, setShowAddTimeRange] = useState<boolean>(true);

  // Only show add time range button if the last time range does not end at the end of the day
  useEffect(() => {
    setShowAddTimeRange(!hasEndOfDay(time));
  }, [time]);

  const handleAddTimeRange = (): void => {
    // Add a new time range with a minute after to for the previous time range to 23:59
    const updatedHours = cloneDeep(hours);
    if (time.length > 0) {
      updatedHours.time.push({
        from: adjustMinutes('add', 1, time[time.length - 1].to),
        to: END_OF_DAY
      });
      setTimeout(() => {
        newFromInputRef.current?.focus();
      }, 50);
    }

    onChange({ day, hours: updatedHours });
  };

  const handleRemoveTimeRange = (index: number): void => {
    const updatedHours = cloneDeep(hours);
    updatedHours.time.splice(index, 1);
    onChange({ day, hours: updatedHours });
  };

  const handleStatusToggleChange = (index: DayOfWeekKey, value: boolean): void => {
    const updatedHours = cloneDeep(hours);
    updatedHours.status = value ? StatusEnum.Open : StatusEnum.Closed;
    // if status is open and there are no time ranges then add a time range with 00:00 to 23:59
    if (updatedHours.status === 'open' && updatedHours.time.length === 0) {
      updatedHours.time.push({ from: START_OF_DAY, to: END_OF_DAY });
    }
    onChange({ day: index, hours: updatedHours });
  };

  const handleTimeChange = (
    index: number,
    field: Exclude<keyof Time, '__typename'>,
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const incomingTime = event.target.value;

    // Update the time value immediately
    const updatedHours = cloneDeep(hours);
    updatedHours.time[index][field] = incomingTime;
    onChange({ day, hours: updatedHours });

    // Use debounce to delay automatic correction until the user has finished typing
    const debouncedAutoCorrection = debounce(() => {
      const validatedTime = autoCorrectTimeInput(index, field, incomingTime, time);

      const validatedUpdatedHours = cloneDeep(hours);
      validatedUpdatedHours.time[index][field] = validatedTime;
      onChange({ day, hours: validatedUpdatedHours });
    }, 300);

    debouncedAutoCorrection();
  };

  return (
    <div key={day} ref={ref} className={styles.DayOfWeek}>
      <div className={styles.FieldSet}>
        <div className={styles.Toggle}>
          <Switch
            aria-labelledby={`${day}-hours-switch-button`}
            isChecked={status === 'open'}
            onUpdate={(e) => handleStatusToggleChange(day, e)}
          />
        </div>
        <div className={styles.Day}>{capitalize(day)}</div>

        <div className={styles.DaysHourContent}>
          {isDayOpen ? (
            time.map((t, index) => (
              /* cannot use any other available keys as it will cause issues when editing time ranges */
              /* eslint-disable react/no-array-index-key */
              <div className={styles.OpenRow} key={index}>
                <div className={styles.TimeRange}>
                  <InputTitleWrap title="Start Time" testId={`start-time-${day}-${index}`}>
                    <input
                      type="time"
                      value={t.from}
                      required
                      onChange={(e) => handleTimeChange(index, 'from', e)}
                      ref={index === time.length - 1 ? newFromInputRef : null}
                    />
                  </InputTitleWrap>
                  <div>To:</div>
                  <InputTitleWrap title="End Time" testId={`end-time-${day}-${index}`}>
                    <input type="time" value={t.to} required onChange={(e) => handleTimeChange(index, 'to', e)} />
                  </InputTitleWrap>
                </div>
                <div className={styles.Action}>
                  {index === 0 ? (
                    showAddTimeRange && (
                      <button
                        className={styles.AddRow}
                        aria-label={`add time range for ${day}`}
                        onClick={() => handleAddTimeRange()}
                        type="button"
                        data-testid={`add-time-range-${day}`}
                      >
                        <i className="mm-plus" />
                      </button>
                    )
                  ) : (
                    <button
                      className={styles.RemoveRow}
                      aria-label={`Remove time ${index} range for ${day}`}
                      onClick={() => handleRemoveTimeRange(index)}
                      type="button"
                      data-testid={`remove-time-range-${day}-${index}`}
                    >
                      <i className="mm-trash-o" />
                    </button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className={styles.ClosedCopy}>Your menu will be unavailable for the day.</div>
          )}
          {error && <div className={styles.Error}>{error}</div>}
        </div>
      </div>
    </div>
  );
});

DayOfWeek.displayName = 'DayOfWeek';

export default DayOfWeek;
